import { Menu, Transition } from "@headlessui/react";
import {
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import { Fragment } from "react";
import { useNavigate } from "react-router-dom";

import { useNavigationPersona } from "@/auth/navigationPersona";
import { usePermission } from "@/auth/permissions/usePermission";
import { Button } from "@/components/Button";
import { canAny, getFeatureNavigationPermission } from "@/features/feature.types";
import { getFeatureById } from "@/features/featureRegistry";

type User = { name?: string; role?: string; avatar?: string } | null;

type Props = {
  user: User;
  onSignOut: () => void;
};

export function NavbarProfileMenu({ user, onSignOut }: Props) {
  const navigate = useNavigate();
  const { can } = usePermission();
  const persona = useNavigationPersona();
  const settings = getFeatureById("settings");
  const canOpenSettings =
    settings && canAny(can, getFeatureNavigationPermission(settings, persona));
  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button
          aria-label={`Account menu${user?.name ? ` for ${user.name}` : ""}`}
          title={user?.name ?? "Account"}
          className="shell-icon"
        >
          {user?.avatar ? (
            <img className="w-7 h-7 rounded-full" src={user.avatar} alt="" />
          ) : (
            <UserCircleIcon className="w-7 h-7" />
          )}
          <div className="sr-only">
            <p className="text-sm font-medium text-slate-900 dark:text-white">
              {user?.name}
            </p>
            <p className="caption capitalize">{user?.role}</p>
          </div>
        </Menu.Button>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-150"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          data-workspace-overlay
          className="shell-popover absolute right-0 z-50 mt-2 w-56 origin-top-right divide-y divide-slate-100 focus:outline-none dark:divide-slate-800"
        >
          <div className="px-3 py-3">
            <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
              {user?.name ?? "Account"}
            </p>
            <p className="mt-0.5 text-xs capitalize text-slate-500">{user?.role}</p>
          </div>
          <div className="px-1 py-1">
            <Menu.Item>
              {({ active }) => (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate("/user-profile")}
                  className={clsx(
                    "w-full !justify-start rounded-md px-2 py-2 text-sm",
                    active
                      ? "bg-blue-500 text-white hover:bg-blue-500 hover:text-white"
                      : "text-slate-900 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-800",
                  )}
                >
                  <UserCircleIcon className="mr-2 w-5 h-5" /> Profile
                </Button>
              )}
            </Menu.Item>
            {canOpenSettings && (
              <Menu.Item>
                {({ active }) => (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => navigate(settings.navigation.path)}
                    className={clsx(
                      "w-full !justify-start rounded-md px-2 py-2 text-sm",
                      active
                        ? "bg-blue-500 text-white hover:bg-blue-500 hover:text-white"
                        : "text-slate-900 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-800",
                    )}
                  >
                    <Cog6ToothIcon className="mr-2 w-5 h-5" /> Settings
                  </Button>
                )}
              </Menu.Item>
            )}
          </div>
          <div className="px-1 py-1">
            <Menu.Item>
              {({ active }) => (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onSignOut}
                  className={clsx(
                    "w-full !justify-start rounded-md px-2 py-2 text-sm",
                    active
                      ? "bg-red-500 text-white hover:bg-red-500 hover:text-white"
                      : "text-slate-900 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-800",
                  )}
                >
                  <ArrowRightOnRectangleIcon className="mr-2 w-5 h-5" /> Sign out
                </Button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
